<template>
  <li v-if="selectedStrap" class="list-block-straps" :style="blockStyles">
    <Observer name="fade-up">
      <div class="list-block-straps__container">
        <div class="list-block-straps__content">
          <!-- Color Selector Slider -->
          <div
            class="list-block-straps__content--vertical-slider slider-colors"
            v-if="colors.length > 1"
          >
            <hooper
              :settings="colorsSettings"
              @slide="updateColors"
              class="colors"
              tabindex="-1"
              ref="colors"
            >
              <slide
                v-for="(color, indx) in colors"
                :key="`${indx + color.id}-dial`"
                :index="indx"
              >
                <button
                  type="button"
                  class="color-swatch"
                  :class="{ selected: currentStrapId === color.id }"
                  @click="colorClicked(color.id)"
                  :style="{ background: color.color }"
                ></button>
              </slide>
              <hooper-navigation
                slot="hooper-addons"
                :class="{
                  'hide-navigation': colors.length <= 4,
                  'hide-desktop-navigation': colors.length <= 5
                }"
              ></hooper-navigation>
            </hooper>
          </div>
          <!-- Watch Image -->
          <div
            class="list-block-straps__content--strap-collage"
            :class="{ soldat: straps.dial.soldat }"
            v-if="
              straps.selectedStrapImage.url &&
                straps.dial.cadran &&
                straps.dial.cadran.url
            "
          >
            <lazy-image
              :class-name="
                straps.dial.soldat ? 'strap-image soldat' : 'strap-image'
              "
              :item="straps.selectedStrapImage"
            />
            <lazy-image
              :item="straps.dial.cadran"
              :draggable="'false'"
              :class-name="
                straps.dial.soldat ? 'dial-image soldat' : 'dial-image'
              "
            />
          </div>
          <!-- Dial Selector Slider -->
          <div
            class="list-block-straps__content--vertical-slider slider-dials"
            v-if="straps && straps.dials && straps.dials.length > 0"
          >
            <hooper
              :settings="dialsSettings"
              ref="dials"
              @slide="updateDials"
              class="dials"
              tabindex="-1"
            >
              <slide
                v-for="(product, indx) in straps.dials"
                :key="`${indx}-dial`"
                :index="indx"
                @click.native="watchClicked(product, indx)"
              >
                <div
                  v-if="product.cadran"
                  class="dial-swatch"
                  :class="{ 'is-selected': selectedDialIndex === indx }"
                >
                  <lazy-image
                    :item="product.cadran"
                    :draggable="'false'"
                  ></lazy-image>
                </div>
              </slide>
              <hooper-navigation
                slot="hooper-addons"
                :class="{
                  'hide-navigation': straps.dials.length <= 2,
                  'hide-desktop-navigation': straps.dials.length <= 3
                }"
              ></hooper-navigation>
            </hooper>
          </div>
        </div>
        <!-- Strap Info -->
        <div class="list-block-straps__info">
          <h2 class="list-block-straps__info--title h5 font-bold">
            {{ selectedStrap.name }}
          </h2>
          <span
            v-if="getProductPrice(selectedStrap)"
            class="list-block-straps__info--price font-price my-3 my-lg-4"
          >
            {{ getProductPrice(selectedStrap) | currency }}
          </span>
          <router-link
            :to="pdpLink"
            class="list-block-straps__info--btn btn -stroke"
          >
            {{ $t('Shop the strap') }}
          </router-link>
        </div>
      </div>
    </Observer>
  </li>
</template>

<script>
import { ReadyHook } from 'vue-asyncready'
import config from 'config'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { getElasticProduct } from 'src/modules/prismic/store/actions'
import ImageManager from 'vue-storefront-prismic-ssr/utilities/ImageManager'
import { Hooper, Slide, Navigation as HooperNavigation } from 'hooper'
import StrapItem from './StrapItem.vue'
import LazyImage from './utils/LazyImage.vue'
import { getProductPrice } from '../utilities'

export default {
  props: ['block', 'i'],
  name: 'PlpStrapsEditorialBlock',
  mixins: [ReadyHook],
  components: {
    Hooper,
    Slide,
    HooperNavigation,
    StrapItem,
    LazyImage
  },
  data() {
    return {
      resolvedRelatedProducts: [],
      currentColors: 0,
      currentDials: 0,
      currentStrapId: null,
      selectedStrap: null,
      selectedDialIndex: 0,
      straps: {
        selectedStrapImage: {},
        dial: {},
        dials: []
      },
      dialsSettings: {
        wheelControl: false,
        itemsToShow: 2,
        itemsToSlide: 2,
        vertical: true,
        breakpoints: {
          991: {
            itemsToShow: 3
          }
        }
      },
      colorsSettings: {
        wheelControl: false,
        itemsToShow: 4,
        itemsToSlide: 2,
        vertical: true,
        breakpoints: {
          768: {
            itemsToShow: 3
          },
          991: {
            itemsToShow: 5
          }
        }
      }
    }
  },
  computed: {
    localizedUrl() {
      const { url } = currentStoreView()
      return url || ''
    },
    blockStyles() {
      return {
        'grid-column':
          this.block.position <= 4
            ? `${this.block.position} / span 2`
            : `1 / span 2`,
        'grid-row': this.block.row
      }
    },
    colors() {
      return this.resolvedRelatedProducts.map(product => ({
        color: product.colorCode,
        id: product.id
      }))
    },
    pdpLink() {
      return `${this.localizedUrl}/product/${
        this.selectedStrap.slug
          ? this.selectedStrap.slug
          : this.selectedStrap.sku
      }`
    }
  },
  methods: {
    getProductPrice: getProductPrice,
    updateColors(payload) {
      this.currentColors = payload.currentSlide
    },
    updateDials(payload) {
      this.currentDials = payload.currentSlide
    },
    watchClicked(product, index) {
      this.straps.dial = product
      this.selectedDialIndex = index
      if (this.$refs.dials && typeof index === 'number') {
        this.$refs.dials.slideTo(index)
      }
    },
    convertImage(url) {
      return config.magento2.imgUrl + url
    },
    getConcatImage(left, right, printPath = true) {
      const extensionRegex = /\.(png|jpg|jpeg|gif|webp|svg|avif)$/i
      const leftMatch = left.match(extensionRegex)
      if (!leftMatch) {
        return null
      }
      let url =
        left.replace(leftMatch[0], '') +
        '_' +
        right.replace('/', '.').toLowerCase() +
        leftMatch[0]
      return printPath ? this.convertImage(url) : url
    },
    checkForStrap() {
      if (!this.selectedStrap || !this.selectedStrap.main_category) {
        return
      }
      const toFind = 'Straps'
      const filtered = this.selectedStrap.main_category.filter(el => {
        return el.name === toFind
      })
      if (filtered.length > 0) {
        this.straps = {
          dial: {},
          dials: [],
          selectedStrapImage: {}
        }

        const relatedProducts = this.selectedStrap.related_products
        if (!relatedProducts || !relatedProducts.length) {
          return
        }
        const imageSrc = this.getConcatImage(
          relatedProducts[0].image,
          this.selectedStrap.sku
        )
        this.straps.selectedStrapImage = {
          url: imageSrc,
          alt: '',
          width: '',
          height: '',
          srcset: ImageManager.getSrcSet(imageSrc, 1000, 400, 100, 1, true),
          lowDefinitionUrl: ImageManager.getLowDefinition(
            imageSrc,
            150,
            1,
            true
          )
        }
        this.straps.dials = relatedProducts
          .filter(product => !!product.dial_image)
          .map(product => {
            const imageCadranSrc = this.convertImage(product.dial_image)
            return {
              sku: product.sku,
              soldat: true,
              cadran: {
                lowDefinitionUrl: ImageManager.getLowDefinition(
                  imageCadranSrc,
                  150,
                  1,
                  true
                ),
                srcset: ImageManager.getSrcSet(
                  imageCadranSrc,
                  1000,
                  400,
                  100,
                  1,
                  true
                ),
                url: imageCadranSrc,
                alt: product.name,
                width: '',
                height: ''
              }
            }
          })
        this.straps.dial = this.straps.dials[0] || {}
        this.selectedDialIndex = 0
      }
    },
    colorClicked(colorId) {
      this.currentStrapId = colorId
      const storeView = currentStoreView()
      getElasticProduct(this.currentStrapId, { size: 1 })
        .then(raw => raw.product[storeView.storeCode])
        .then(fullStrap => {
          if (fullStrap) {
            this.selectedStrap = fullStrap
            this.checkForStrap()
          }
        })
    },
    async initializeStrap() {
      if (this.block && this.block.product && this.block.product.routeParam) {
        const storeView = currentStoreView()
        try {
          const raw = await getElasticProduct(this.block.product.routeParam, {
            size: 1
          })
          const fullProductStrap = raw?.product?.[storeView.storeCode]

          if (fullProductStrap) {
            this.selectedStrap = fullProductStrap
            this.currentStrapId = fullProductStrap.id

            // Set color variations
            if (
              fullProductStrap.colors_variations &&
              fullProductStrap.colors_variations.length
            ) {
              this.resolvedRelatedProducts = [
                fullProductStrap,
                ...fullProductStrap.colors_variations
              ]
            } else {
              this.resolvedRelatedProducts = [fullProductStrap]
            }

            // Check for straps and dials
            this.checkForStrap()
          }
        } catch (error) {
          console.error('Error fetching strap product:', error)
        }
      }
      this.$ready()
    }
  },
  created() {
    this.initializeStrap()
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

@mixin arrowNext {
  &:before {
    display: block;
    content: '';
    position: absolute;
    top: 40%;
    left: 50%;
    width: 1.4rem;
    height: 1.4rem;
    border-right: 1px solid var(--zenith-theme-primary);
    border-bottom: 1px solid var(--zenith-theme-primary);
    transform: translate(-50%, -50%) rotate(45deg);
    transition: all 0.2s ease-in-out;

    @include bp(max md) {
      width: 1rem;
      height: 1rem;
    }
  }
  &.is-disabled {
    opacity: 0;
    pointer-events: none;
  }

  &:hover,
  &:focus {
    &:before {
      transform: translate(-50%, -50%) rotate(45deg) scale(1.2);
    }
  }
}

@mixin arrowPrev {
  &:before {
    display: block;
    content: '';
    position: absolute;
    top: 60%;
    left: 50%;
    width: 1.4rem;
    height: 1.4rem;
    border-left: 1px solid var(--zenith-theme-primary);
    border-top: 1px solid var(--zenith-theme-primary);
    transform: translate(-50%, -50%) rotate(45deg);
    transition: all 0.2s ease-in-out;

    @include bp(max md) {
      width: 1rem;
      height: 1rem;
    }
  }
  &.is-disabled {
    opacity: 0;
    pointer-events: none;
  }

  &:hover,
  &:focus {
    &:before {
      transform: translate(-50%, -50%) rotate(45deg) scale(1.2);
    }
  }
}

.list-block-straps {
  position: relative;
  margin-bottom: 3rem;

  @include bp(max md) {
    grid-column: 1 / 3 !important;
  }

  /deep/ .obs {
    height: 100%;
  }

  &__container {
    padding: 1rem 2rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    border: 0.1rem solid var(--zenith-thin-border-color);

    @include bp(lg) {
      padding: 1rem 4rem;
    }
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    margin-bottom: 2.5rem;

    &--vertical-slider {
      position: relative;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      flex-direction: column;
      flex-basis: 20%;

      &.slider-colors {
        padding: 3rem 1rem 3rem 0;
      }

      &.slider-dials {
        padding: 3rem 0 3rem 1rem;

        .dial-swatch {
          opacity: 0.5;
          pointer-events: none;

          &.is-selected {
            opacity: 1;
          }
        }

        /deep/ .hooper-slide {
          cursor: pointer;

          &:hover .dial-swatch {
            opacity: 1;
          }
        }
      }

      /deep/ .hooper-list {
        display: grid;
      }

      .colors,
      .dials {
        height: 100%;
      }

      /deep/ .hooper-slide {
        transition: all 0.2s ease-in-out;

        &:hover {
          transform: scale(1.1);
        }
      }

      .color-swatch {
        height: 3.2rem;
        width: 3.2rem;
        border-radius: 50%;
        border: none;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        background: none;
        padding: 0;

        &.selected {
          outline: 0.1rem solid var(--zenith-thin-border-color);
          outline-offset: 4px;
        }
      }

      /deep/ .hooper-navigation {
        &.hide-navigation {
          display: none;
        }

        &.hide-desktop-navigation {
          display: none;
        }

        .hooper-prev {
          position: absolute;
          left: 0;
          right: 0;
          top: -3.3rem;
          svg {
            display: none;
          }
          @include arrowPrev;
        }

        .hooper-next {
          position: absolute;
          left: 0;
          right: 0;
          bottom: -3.5rem;
          svg {
            display: none;
          }
          @include arrowNext;
        }
      }
    }

    &--strap-collage {
      position: relative;
      display: flex;
      flex: 1;
      flex-basis: 60%;
      align-items: center;
      justify-content: center;

      /deep/ .strap-image,
      /deep/ .dial-image {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        max-width: 25rem;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 100%;
          height: auto;
          object-fit: contain;
        }
      }

      /deep/ .dial-image {
        z-index: 2;
      }
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 2rem;
    text-align: center;
    border-top: 1px solid var(--zenith-btn-disabled, #e0e0e0);

    &--title {
      color: var(--zenith-component-product-item-title);
      font-weight: 500;
      margin-bottom: 0;
    }

    &--price {
      color: var(--zenith-component-product-item-price);
      margin: 0 0 1.5rem;
    }

    &--btn {
      font-size: 1.2rem;
      min-height: 3rem;
    }
  }
}

.hooper {
  outline: none;

  /deep/ .hooper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
